import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { ArrowLeft, Save, Pencil, Trash2, FileText, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { generateTasmiPDF } from '@/utils/pdfGenerator';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const JUZ_OPTIONS = Array.from({ length: 30 }, (_, i) => i + 1);

const PENILAIAN_OPTS = ['A', 'A-', 'B', 'B-', 'Remedial'];
const nilaiTasmi = (t) => t?.penilaian || (t?.keterangan === 'Remedial' ? 'Remedial' : (t?.keterangan ? 'A' : '—'));
const nilaiCls = (v) => v === 'Remedial' ? 'bg-red-100 text-red-600' : v === '—' ? 'bg-gray-100 text-gray-500' : 'bg-emerald-100 text-emerald-700';

const KELAS_MAP = { D: "I'dad", C: 'Uula', B: 'Wustha', A: 'Ulya', M: 'Ali/Mahir' };

const EMPTY = {
  santri_id: '', tanggal: new Date().toISOString().split('T')[0],
  tipe: '1juz', juz: '', jumlah_juz: '', juz_dari: '', juz_sampai: '',
  jam_mulai: '', jam_selesai: '', tempat: '', penyimak: '',
  jumlah_jali: 0, penilaian: 'A',
};

function formatJuz(t) {
  if (!t) return '-';
  if (t.tipe === 'multi') return `Juz ${t.juz_dari}–${t.juz_sampai} (${t.jumlah_juz} Juz)`;
  return `Juz ${t.juz}`;
}
function formatDurasi(t) {
  if (!t?.jam_mulai && !t?.jam_selesai) return '—';
  return `${t.jam_mulai || '?'} – ${t.jam_selesai || '?'} WIB`;
}

export default function InputTasmi() {
  const navigate = useNavigate();
  const [santriList, setSantriList] = useState([]);
  const [halaqahList, setHalaqahList] = useState([]);
  const [riwayat, setRiwayat] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [settings, setSettings] = useState({});
  // Kartu preview state
  const [previewSantriId, setPreviewSantriId] = useState(null);
  const [printingId, setPrintingId] = useState(null);
  const [showKartu, setShowKartu] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/santri`),
      axios.get(`${API}/halaqah`),
      axios.get(`${API}/settings`),
    ]).then(([s, h, st]) => {
      setSantriList(s.data);
      setHalaqahList(h.data);
      setSettings(st.data || {});
    }).catch(() => {});
    loadRiwayat();
  }, []);

  const loadRiwayat = async () => {
    try { const r = await axios.get(`${API}/tasmi`); setRiwayat(r.data); } catch {}
  };

  // Santri merged with halaqah name
  const santriMap = Object.fromEntries(
    santriList.map(s => [s.id, {
      ...s,
      halaqah_nama: halaqahList.find(h => h.id === s.halaqah_id)?.nama || '',
    }])
  );

  const selectedSantri = santriMap[form.santri_id];
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.santri_id) { toast.error('Pilih santri terlebih dahulu'); return; }
    if (!form.tanggal)   { toast.error('Tanggal wajib diisi'); return; }
    if (form.tipe === '1juz' && !form.juz) { toast.error('Pilih nomor Juz'); return; }
    if (form.tipe === 'multi' && (!form.jumlah_juz || !form.juz_dari || !form.juz_sampai)) {
      toast.error('Lengkapi data multi-juz'); return;
    }

    setSaving(true);
    const payload = {
      ...form,
      halaqah_id: selectedSantri?.halaqah_id || '',
      juz: form.tipe === '1juz' ? Number(form.juz) : null,
      jumlah_juz: form.tipe === 'multi' ? Number(form.jumlah_juz) : null,
      juz_dari: form.tipe === 'multi' ? Number(form.juz_dari) : null,
      juz_sampai: form.tipe === 'multi' ? Number(form.juz_sampai) : null,
      jumlah_jali: Number(form.jumlah_jali) || 0,
      penilaian: form.penilaian,
      keterangan: form.penilaian === 'Remedial' ? 'Remedial' : 'Lulus',
    };

    try {
      if (editingId) {
        await axios.put(`${API}/tasmi/${editingId}`, payload);
        toast.success("Data Tasmi' diperbarui");
      } else {
        await axios.post(`${API}/tasmi`, payload);
        toast.success("Tasmi' berhasil disimpan");
      }
      setForm(EMPTY); setEditingId(null);
      await loadRiwayat();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const handleEdit = (t) => {
    setEditingId(t.id);
    setForm({
      santri_id: t.santri_id, tanggal: t.tanggal,
      tipe: t.tipe || '1juz', juz: t.juz || '', jumlah_juz: t.jumlah_juz || '',
      juz_dari: t.juz_dari || '', juz_sampai: t.juz_sampai || '',
      jam_mulai: t.jam_mulai || '', jam_selesai: t.jam_selesai || '',
      tempat: t.tempat || '', penyimak: t.penyimak || '',
      jumlah_jali: t.jumlah_jali ?? 0, penilaian: nilaiTasmi(t) === '—' ? 'A' : nilaiTasmi(t),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Hapus data tasmi' ini?")) return;
    try { await axios.delete(`${API}/tasmi/${id}`); toast.success('Data dihapus'); await loadRiwayat(); }
    catch { toast.error('Gagal menghapus'); }
  };

  const handleUnduhPDF = async (santriId) => {
    const s = santriMap[santriId];
    if (!s) return;
    const list = riwayat.filter(t => t.santri_id === santriId);
    setPrintingId(santriId);
    try {
      await generateTasmiPDF(s, list, settings);
      toast.success(`PDF ${s.name} berhasil diunduh`);
    } catch { toast.error('Gagal mengunduh PDF'); }
    finally { setPrintingId(null); }
  };

  // Santri who have tasmi' records (unique)
  const santriWithTasmi = [...new Set(riwayat.map(t => t.santri_id))]
    .map(id => santriMap[id]).filter(Boolean);

  // Preview kartu modal data
  const previewSantri = santriMap[previewSantriId];
  const previewTasmi  = riwayat.filter(t => t.santri_id === previewSantriId);

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 pb-28 md:pb-8">
        {/* Header */}
        <div className="bg-gradient-to-br from-emerald-950 via-emerald-800 to-emerald-700 px-5 pt-12 pb-6 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/5 rounded-full pointer-events-none" />
          <div className="relative flex items-center gap-3">
            <button data-testid="tasmi-back-btn" onClick={() => navigate('/guru')}
              className="p-2 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors">
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="font-playfair text-xl font-bold text-white">Input Tasmi'</h1>
              <p className="text-emerald-300 text-xs mt-0.5">Pencatatan ujian hafalan santri</p>
            </div>
          </div>
        </div>

        <div className="px-5 py-5 space-y-4">
          {/* ── FORM ── */}
          <form onSubmit={handleSubmit} className="space-y-4">

            {/* Section: Data Diri */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-100">
                <h2 className="text-sm font-bold text-emerald-800">Data Diri</h2>
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nama Santri</label>
                  <select data-testid="tasmi-santri-select"
                    value={form.santri_id} onChange={e => setF('santri_id', e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400">
                    <option value="">— Pilih Santri —</option>
                    {santriList.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.name}{s.kelas ? ` (${s.kelas})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Tanggal</label>
                  <input type="date" data-testid="tasmi-tanggal"
                    value={form.tanggal} onChange={e => setF('tanggal', e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Halaqah</label>
                  <div className="w-full border border-gray-100 rounded-xl px-3 py-2.5 text-sm bg-gray-50 text-gray-500 min-h-[40px]">
                    {selectedSantri?.halaqah_nama || 'Otomatis terisi setelah memilih santri'}
                  </div>
                </div>
              </div>
            </div>

            {/* Section: Data Tasmi' */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-100">
                <h2 className="text-sm font-bold text-emerald-800">Data Tasmi'</h2>
              </div>
              <div className="p-4 space-y-3">
                {/* Toggle tipe */}
                <div className="flex rounded-xl border border-gray-200 overflow-hidden">
                  {[{ val: '1juz', label: '1 Juz' }, { val: 'multi', label: 'Lebih dari 1 Juz' }].map(o => (
                    <button type="button" key={o.val} data-testid={`tasmi-tipe-${o.val}`}
                      onClick={() => setF('tipe', o.val)}
                      className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${
                        form.tipe === o.val ? 'bg-emerald-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
                      }`}>{o.label}</button>
                  ))}
                </div>

                {/* 1 Juz */}
                {form.tipe === '1juz' && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Juz</label>
                    <select data-testid="tasmi-juz-select" value={form.juz} onChange={e => setF('juz', e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400">
                      <option value="">— Pilih Juz —</option>
                      {JUZ_OPTIONS.map(j => <option key={j} value={j}>Juz {j}</option>)}
                    </select>
                  </div>
                )}

                {/* Multi Juz */}
                {form.tipe === 'multi' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Jumlah Juz</label>
                      <input type="number" min="2" max="30" placeholder="Contoh: 5"
                        data-testid="tasmi-jumlah-juz"
                        value={form.jumlah_juz} onChange={e => setF('jumlah_juz', e.target.value)}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {[['juz_dari','Juz Dari'],['juz_sampai','Juz Sampai']].map(([key,label]) => (
                        <div key={key}>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">{label}</label>
                          <select value={form[key]} onChange={e => setF(key, e.target.value)}
                            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400">
                            <option value="">—</option>
                            {JUZ_OPTIONS.map(j => <option key={j} value={j}>Juz {j}</option>)}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Durasi */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Durasi (WIB)</label>
                  <div className="flex items-center gap-2">
                    <input type="text" placeholder="08.00" data-testid="tasmi-jam-mulai"
                      value={form.jam_mulai} onChange={e => setF('jam_mulai', e.target.value)}
                      className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                    <span className="text-gray-400 text-sm font-medium">–</span>
                    <input type="text" placeholder="10.00" data-testid="tasmi-jam-selesai"
                      value={form.jam_selesai} onChange={e => setF('jam_selesai', e.target.value)}
                      className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                    <span className="text-gray-500 text-xs font-semibold">WIB</span>
                  </div>
                </div>

                {/* Tempat */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Tempat Tasmi'</label>
                  <input type="text" placeholder="Contoh: Masjid Al-Ikhlas" data-testid="tasmi-tempat"
                    value={form.tempat} onChange={e => setF('tempat', e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                </div>

                {/* Penyimak */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Penyimak</label>
                  <input type="text" placeholder="Nama penyimak" data-testid="tasmi-penyimak"
                    value={form.penyimak} onChange={e => setF('penyimak', e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                </div>
              </div>
            </div>

            {/* Section: Penilaian */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-100">
                <h2 className="text-sm font-bold text-emerald-800">Penilaian</h2>
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Jumlah Kesalahan Jali</label>
                  <input type="number" min="0" data-testid="tasmi-jumlah-jali"
                    value={form.jumlah_jali} onChange={e => setF('jumlah_jali', e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">Penilaian</label>
                  <div className="grid grid-cols-5 gap-1.5">
                    {PENILAIAN_OPTS.map(v => (
                      <button type="button" key={v} data-testid={`tasmi-nilai-${v.replace('-','minus').toLowerCase()}`}
                        onClick={() => setF('penilaian', v)}
                        className={`py-2.5 rounded-xl text-xs font-bold border-2 transition-all ${
                          form.penilaian === v
                            ? v === 'Remedial' ? 'bg-red-500 text-white border-red-500' : 'bg-emerald-600 text-white border-emerald-600'
                            : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                        }`}>{v}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <button type="submit" data-testid="tasmi-simpan-btn" disabled={saving}
              className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-2xl shadow-md transition-all disabled:opacity-60 flex items-center justify-center gap-2">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <Save size={17} />}
              {editingId ? "Perbarui Data Tasmi'" : "Simpan Data Tasmi'"}
            </button>
            {editingId && (
              <button type="button" onClick={() => { setEditingId(null); setForm(EMPTY); }}
                className="w-full py-3 bg-gray-100 text-gray-600 font-semibold rounded-2xl text-sm">
                Batal Edit
              </button>
            )}
          </form>

          {/* ── RIWAYAT SEMUA SANTRI ── */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-100">
              <h2 className="text-sm font-bold text-gray-700">Riwayat Tasmi' ({riwayat.length} data)</h2>
            </div>

            {/* Kartu cetak per santri */}
            {santriWithTasmi.length > 0 && (
              <div className="p-4 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Cetak Kartu Santri</p>
                <div className="flex flex-wrap gap-2">
                  {santriWithTasmi.map(s => (
                    <div key={s.id} className="flex items-center gap-1.5 bg-gray-50 rounded-xl px-2 py-1.5 border border-gray-100">
                      <span className="text-xs font-medium text-gray-700">{s.name}</span>
                      <button data-testid={`tasmi-lihat-kartu-${s.id}`}
                        onClick={() => { setPreviewSantriId(s.id); setShowKartu(true); }}
                        className="p-1 bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-100 transition-colors">
                        <Eye size={12} />
                      </button>
                      <button data-testid={`tasmi-unduh-pdf-${s.id}`}
                        onClick={() => handleUnduhPDF(s.id)} disabled={printingId === s.id}
                        className="p-1 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50">
                        {printingId === s.id
                          ? <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                          : <FileText size={12} />}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {riwayat.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">Belum ada data tasmi'</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px] text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      {['#','Santri','Juz','Durasi','Tgl','Tempat','Penyimak','Jali','Nilai','']. map(h => (
                        <th key={h} className="px-3 py-2.5 text-left text-gray-500 font-semibold whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {riwayat.map((t, i) => {
                      const s = santriMap[t.santri_id];
                      return (
                        <tr key={t.id} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="px-3 py-2.5 text-gray-400">{i + 1}</td>
                          <td className="px-3 py-2.5 font-medium text-gray-700 whitespace-nowrap">{s?.name || '—'}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap">{formatJuz(t)}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap">{formatDurasi(t)}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap">{t.tanggal}</td>
                          <td className="px-3 py-2.5">{t.tempat || '—'}</td>
                          <td className="px-3 py-2.5">{t.penyimak || '—'}</td>
                          <td className="px-3 py-2.5 text-center">{t.jumlah_jali ?? '—'}</td>
                          <td className="px-3 py-2.5">
                            <span className={`font-bold px-2 py-0.5 rounded-full text-[10px] ${nilaiCls(nilaiTasmi(t))}`}>{nilaiTasmi(t)}</span>
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1.5">
                              <button onClick={() => handleEdit(t)} data-testid={`tasmi-edit-${t.id}`}
                                className="p-1.5 bg-amber-50 hover:bg-amber-100 text-amber-600 rounded-lg transition-colors">
                                <Pencil size={12} />
                              </button>
                              <button onClick={() => handleDelete(t.id)} data-testid={`tasmi-delete-${t.id}`}
                                className="p-1.5 bg-red-50 hover:bg-red-100 text-red-500 rounded-lg transition-colors">
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── PREVIEW KARTU TASMI' (FULLSCREEN MODAL) ── */}
      <AnimatePresence>
        {showKartu && previewSantri && (
          <motion.div data-testid="tasmi-kartu-modal"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-gray-900 flex flex-col">

            {/* Modal Header */}
            <div className="bg-gradient-to-r from-emerald-950 to-emerald-800 px-5 pt-10 pb-4 flex items-center gap-3 flex-shrink-0">
              <button data-testid="kartu-tutup-btn"
                onClick={() => setShowKartu(false)}
                className="p-2 bg-white/10 rounded-xl text-white hover:bg-white/20">
                <ArrowLeft size={18} />
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-sm truncate">Kartu Tasmi' — {previewSantri.name}</p>
                <p className="text-emerald-300 text-[11px]">Kelas {previewSantri.kelas} · {KELAS_MAP[previewSantri.kelas] || ''}</p>
              </div>
              <button data-testid="kartu-unduh-pdf-btn"
                onClick={() => handleUnduhPDF(previewSantriId)} disabled={printingId === previewSantriId}
                className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-xl transition-colors disabled:opacity-60">
                {printingId === previewSantriId
                  ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <FileText size={14} />}
                Unduh PDF
              </button>
            </div>

            {/* Kartu content */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Santri Info */}
              <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {[
                    ['Nama Santri', previewSantri.name],
                    ['NIS', previewSantri.nis || '-'],
                    ['Kelas', previewSantri.kelas ? `${previewSantri.kelas} – ${KELAS_MAP[previewSantri.kelas]}` : '-'],
                    ['Halaqah', previewSantri.halaqah_nama || '-'],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <p className="text-gray-400 font-medium">{label}</p>
                      <p className="text-gray-800 font-semibold mt-0.5">{value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tabel */}
              {previewTasmi.length === 0 ? (
                <div className="bg-white rounded-2xl p-8 text-center text-gray-400 text-sm">
                  Belum ada data tasmi' untuk santri ini
                </div>
              ) : (
                <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[580px] text-xs">
                      <thead>
                        <tr className="bg-emerald-900 text-white">
                          {['No','Juz','Durasi','Tanggal','Tempat','Penyimak','Jali','Nilai'].map(h => (
                            <th key={h} className="px-3 py-3 text-left font-semibold whitespace-nowrap text-[11px]">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewTasmi.map((t, i) => (
                          <tr key={t.id} className={i % 2 === 0 ? 'bg-white' : 'bg-emerald-50/40'}>
                            <td className="px-3 py-2.5 text-gray-400">{i + 1}</td>
                            <td className="px-3 py-2.5 font-medium whitespace-nowrap">{formatJuz(t)}</td>
                            <td className="px-3 py-2.5 whitespace-nowrap text-gray-600">{formatDurasi(t)}</td>
                            <td className="px-3 py-2.5 whitespace-nowrap">{t.tanggal || '—'}</td>
                            <td className="px-3 py-2.5">{t.tempat || '—'}</td>
                            <td className="px-3 py-2.5">{t.penyimak || '—'}</td>
                            <td className="px-3 py-2.5 text-center">{t.jumlah_jali ?? '—'}</td>
                            <td className="px-3 py-2.5">
                              <span className={`font-bold px-2 py-0.5 rounded-full text-[10px] ${
                                t.keterangan === 'Lulus' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'
                              }`}>{t.keterangan}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
}
